import classNames from 'classnames';
import isDeepEqual from 'lodash.isequal';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { memo, useEffect, useState } from 'react';
import { FiArrowDown } from 'react-icons/fi';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { useEntity } from '../../../../lib/entity';
import { useMessages } from '../../../../lib/messages';
import { PER_PAGE } from '../../../../lib/supabase';
import { parseUTC } from '../../../../lib/utils/format';
import getIdFromSlug from '../../../../lib/utils/getIdFromSlug';
import messageSearch from '../../../../lib/utils/messageSearch';
import Button from '../../../Button';
import { MessageSkeleton } from '../../ChatPage/Message';

const Message = dynamic(() => import('../../ChatPage/Message'), { loading: MessageSkeleton });

function EntityMessages({ searchQuery = '', disableFetch, onCreate, isLoading: loading, show }) {
  const router = useRouter();
  const { route } = router;
  const { messageId, roomSlug, entitySlug } = router.query;

  const entityId = getIdFromSlug(entitySlug);

  const roomId = getIdFromSlug(roomSlug);

  const showInfo = ['/e/[type]/[entitySlug]', '/[username]'].includes(route);

  const entity = useEntity().entity;
  const entityRooms = useEntity().state.entityRooms;

  const stateFetchMore = useMessages().state.canFetchMore;
  const stateIsLoading = useMessages().state.isLoading;
  const users = useMessages().state.users;
  const byId = useMessages().state.messages.byId;
  const messageChildren = useMessages().messageChildren;
  const parentMessage = useMessages().parentMessage;
  const parentChildren = useMessages().parentChildren;
  const messages = useMessages().messages;
  const messagesRoom = useMessages().messagesRoom;
  const fetchEntityMessages = useMessages().fetchEntityMessages;
  const fetchUserMessages = useMessages().fetchUserMessages;
  const currentProfile = useMessages().currentProfile;
  const messagesEndRef = useMessages().messagesEndRef;
  const messagesStartRef = useMessages().messagesStartRef;
  const dispatch = useMessages().dispatch;

  const getRoomIds = () => {
    if (messageId) {
      const msg = byId[messageId];
      if (msg) {
        return [msg.room_id];
      }
    }
    if (roomId) {
      return [roomId];
    }

    return entityRooms[entityId]?.rooms.ids ?? [];
  };

  const roomIds = getRoomIds();

  const isLoading = loading || stateIsLoading;

  const [canFetchMore, setCanFetchMore] = useState(false);

  const [range, setRange] = useState({
    rangeFrom: 0,
    rangeTo: PER_PAGE,
  });

  const getMessages = () => {
    if (messageId) {
      const message = byId[messageId];
      if (message) {
        if (parentMessage.id) {
          return messages
            .filter(
              (m) =>
                parentChildren.find((pCM) => message.id === pCM.id && m.id === pCM.id) ||
                messageChildren.find((cM) => m.id === cM.id),
            )

            .sort((a, b) => parseUTC(a.inserted_at) - parseUTC(b.inserted_at));
        }
        return messageChildren.sort((a, b) => parseUTC(a.inserted_at) - parseUTC(b.inserted_at));
      }
      return [];
    }

    if (searchQuery.length) {
      return messageSearch(messages, searchQuery);
    }

    return messages;
  };

  const allMessages = getMessages();

  const isEmpty = !allMessages.length;

  const messageContentClasses = classNames('room-messages relative');

  const handleRefresh = () => {
    if (currentProfile.id) {
      return fetchUserMessages(dispatch, currentProfile.id);
    }

    if (roomId && roomIds.length) {
      return fetchEntityMessages(dispatch, roomIds, { rangeFrom: 0, rangeTo: PER_PAGE }, users);
    }
    return Promise.resolve();
  };

  const handleFetchMore = () => {
    if (currentProfile.id) {
      return fetchUserMessages(dispatch, currentProfile.id, {
        rangeFrom: range.rangeFrom + PER_PAGE + 1,
        rangeTo: range.rangeTo + PER_PAGE + 1,
      }).then(() =>
        setRange({
          rangeFrom: range.rangeFrom + PER_PAGE + 1,
          rangeTo: range.rangeTo + PER_PAGE + 1,
        }),
      );
    }

    if (roomIds.length) {
      return fetchEntityMessages(
        dispatch,
        roomIds,
        {
          rangeFrom: range.rangeFrom + PER_PAGE + 1,
          rangeTo: range.rangeTo + PER_PAGE + 1,
        },
        users,
      ).then(() =>
        setRange({
          rangeFrom: range.rangeFrom + PER_PAGE + 1,
          rangeTo: range.rangeTo + PER_PAGE + 1,
        }),
      );
    }
    return Promise.resolve();
  };

  useEffect(() => {
    setCanFetchMore(stateFetchMore && !isLoading && !disableFetch);
  }, [disableFetch, isLoading, stateFetchMore]);

  return (
    <>
      <div
        ref={messagesStartRef}
        className="scroll-snap"
        style={{
          height: 0,
        }}
      />
      <section className={messageContentClasses} id="messages">
        {isLoading && [...Array(5).keys()].map((i) => <MessageSkeleton key={i} />)}
        {!isLoading && isEmpty && !messageId && (
          <section className="text-center p-4 space-y-4">
            <h2 className="text-xl text-gray-400">
              💭
              <br />
              No discussions going on right now for{' '}
              {roomId ? 'this room' : <strong>{entity.title}</strong>}
            </h2>
            {onCreate && <Button onClick={onCreate}>Start one</Button>}
          </section>
        )}
        <PullToRefresh
          isPullable={!messageId}
          pullDownThreshold={75}
          pullingContent={
            <section className="text-center space-y-4 py-2">
              <FiArrowDown className="w-6 h-6 mx-auto" />
              Pull down to refresh
            </section>
          }
          onRefresh={handleRefresh}
          canFetchMore={canFetchMore}
          onFetchMore={handleFetchMore}
          maxPullDownDistance={300}
        >
          {show &&
            allMessages.map((m) => (
              <Message
                key={m.id}
                message={m}
                room={messagesRoom}
                showInfo={showInfo}
                mainThread={messageId === `${m.id}`}
              />
            ))}
          <div ref={messagesEndRef} className="scroll-snap" style={{ height: 0 }} />
        </PullToRefresh>
      </section>
    </>
  );
}

// EntityMessages.whyDidYouRender = true;

export default memo(EntityMessages, isDeepEqual);
